import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { visibleBody } from "../api/redaction";
import type { HistoryEntry, HistoryReason } from "../api/types";
import { useServices } from "../app/services";
import { useResource } from "../app/useResource";
import { EnqueueButton } from "../components/cards";
import { Icon } from "../components/Icon";
import { EmptyState, Field, PageHeader, Pill, ResourceView, Thumb } from "../components/ui";
import { HISTORY_REASON_LABEL, performerNames } from "../lib/labels";
import { formatDateTime } from "../lib/time";

type HistoryQuery = { date: string; title: string; performer: string };

/**
 * これまでにVCで流れた曲（共有の再生履歴）。日付・曲名・歌った人で絞り込む。
 * 載せるのは曲・歌唱・再生日時・追加した人・公開中の紹介だけ（VCの参加者や個人の閲覧は載せない）
 */
export function HistoryPage() {
  const [params, setParams] = useSearchParams();
  const filter = { date: params.get("date") ?? "", title: params.get("title") ?? "", performer: params.get("performer") ?? "" };
  const [form, setForm] = useState(filter);
  const key = JSON.stringify(filter);
  const apply = (next: typeof form) => {
    const sp: Record<string, string> = {};
    Object.entries(next).forEach(([k, v]) => v.trim() && (sp[k] = v.trim()));
    setParams(sp);
  };
  const active = Object.values(filter).some(Boolean);

  return (
    <div className="page">
      <PageHeader title="履歴" lead="これまでにVCで流れた曲。日付・曲名・歌った人で探せます。" />
      <form
        className="filter-form"
        role="search"
        aria-label="履歴の絞り込み"
        onSubmit={(e) => {
          e.preventDefault();
          apply(form);
        }}
      >
        <Field label="日付">
          {({ id }) => (
            <input
              id={id}
              type="date"
              className="input input--date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          )}
        </Field>
        <Field label="曲名">
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="曲名の一部"
            />
          )}
        </Field>
        <Field label="歌った人">
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={form.performer}
              onChange={(e) => setForm({ ...form, performer: e.target.value })}
              placeholder="名前の一部"
            />
          )}
        </Field>
        <div className="filter-form__actions">
          <button type="submit" className="btn btn-primary btn-small">
            <Icon name="search" className="btn-icon" />
            絞り込む
          </button>
          {active && (
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={() => {
                const empty = { date: "", title: "", performer: "" };
                setForm(empty);
                apply(empty);
              }}
            >
              条件を外す
            </button>
          )}
        </div>
      </form>
      <HistoryResults key={key} filter={filter} active={active} />
    </div>
  );
}

/** 絞り込みを変えたときだけ作り直す。前の条件の応答は新しい一覧に混ぜない。 */
function HistoryResults({ filter, active }: { filter: HistoryQuery; active: boolean }) {
  const { api } = useServices();
  const [pageCount, setPageCount] = useState(1);
  const res = useResource(
    "history",
    async () => {
      // 自動更新でも開いたページ数を維持し、全ページの取得に成功してから一括で置き換える。
      // 先頭への新着追加や削除でページの境界が動くので、追加分だけを古い一覧へ継ぎ足さない。
      const pages = await Promise.all(
        Array.from({ length: pageCount }, (_, i) => api.getHistory({ ...filter, page: i + 1 })),
      );
      const items = new Map<string, HistoryEntry>();
      let last = pages[0];
      for (const page of pages) {
        for (const item of page.items) {
          if (!items.has(item.id)) items.set(item.id, item);
        }
        last = page;
        if (!page.has_more) break;
      }
      return { items: [...items.values()], page: last.page, has_more: last.has_more };
    },
    [],
    ["playback"],
  );
  const loadingMore = res.status === "ready" && res.refreshing && pageCount > res.data.page;
  const loadMore = () => {
    if (res.status !== "ready" || loadingMore || !res.data.has_more) return;
    setPageCount(res.data.page + 1);
    res.reload();
  };

  return (
    <ResourceView resource={res} what="履歴" rows={5}>
      {(d) => d.items.length === 0 ? (
        <EmptyState title={active ? "条件に合う曲は流れていません" : "まだ何も流れていません"} icon="history" />
      ) : (
        <>
          <ol className="history-list" aria-label="再生履歴（新しい順）">
            {d.items.map((h) => (
              <HistoryRow key={h.id} entry={h} />
            ))}
          </ol>
          {d.has_more && (
            <p className="section-more">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={loadMore}
                disabled={loadingMore}
                aria-busy={loadingMore || undefined}
              >
                もっと見る
              </button>
            </p>
          )}
        </>
      )}
    </ResourceView>
  );
}

const REASON_TONE: Record<string, "calm" | "info" | "warn" | "danger"> = {
  completed: "calm",
  skipped: "info",
  stopped: "info",
  failed: "danger",
  interrupted: "warn",
};

function HistoryRow({ entry: h }: { entry: HistoryEntry }) {
  const p = h.item.performance;
  const body = visibleBody(h.item.post);
  const reason = (h.reason ?? "") as HistoryReason;
  return (
    <li className="history-row">
      <time className="history-row__time" dateTime={h.started_at}>
        {formatDateTime(h.started_at)}
      </time>
      <div className="history-row__main">
        <Thumb videoId={p.segment.video_id} title={p.work.title} size="s" />
        <div className="history-row__body">
          <p className="history-row__title">
            <Link to={`/performances/${p.id}`}>{p.work.title}</Link>
          </p>
          <p className="history-row__sub">
            {performerNames(p)} ・ {h.item.author_name} さんが追加
          </p>
          {reason ? (
            <Pill tone={REASON_TONE[reason] ?? "info"}>{HISTORY_REASON_LABEL[reason] ?? reason}</Pill>
          ) : (
            !h.ended_at && <Pill tone="info">流れている途中</Pill>
          )}
          {h.item.post && body !== null && (
            <p className="history-row__intro">
              「{body}」— {h.item.post.author_name} さん
            </p>
          )}
        </div>
        <EnqueueButton performance={p} compact />
      </div>
    </li>
  );
}
